import React from 'react';

import { Button, Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle } from '@material-ui/icons';

import useBlocRefHook from '../../utils/useBlocRef.hook';

const useStyles = makeStyles(() => {
  return {
    emailInput: {
      border: '1px solid #ccc',
      borderRadius: '8px',
      display: 'block',
      fontSize: '16px',
      fontWeight: 'medium',
      marginRight: 0,
      marginTop: '4px',
      outline: 'none',
      padding: '8px 8px',
      textAlign: 'center',
      width: '-webkit-fill-available',
    },

    errMessageContainer: {
      background: '#fef2f2',
      border: '1px solid #dc2626',
      borderRadius: '8px',
      color: '#dc2626',
      fontSize: '14px',
      marginTop: '12px',
      padding: '8px 8px',
    },

    inputClass: {
      border: '1px solid #ccc',
      borderRadius: '8px',
      display: 'block',
      fontSize: '24px',
      fontWeight: 'bold',
      letterSpacing: '10px',
      marginRight: 0,
      marginTop: '4px',
      outline: 'none',
      padding: '8px 8px',
      textAlign: 'center',
      width: 'calc(100% - 18px)',
    },

    inputLabel: {
      color: '#999',
      fontWeight: 'medium',
    },

    mfaCode: {
      background: '#bbf7d0',
      border: '2px dashed #16a34a',
      borderRadius: '8px',
      color: '#16a34a',
      display: 'block',
      fontSize: '24px',
      fontWeight: 'bold',
      letterSpacing: '10px',
      marginRight: 0,
      outline: 'none',
      padding: '8px 8px',
      textAlign: 'center',
      width: '-webkit-fill-available',
    },
  };
});

export default function EmailChangeDialog({ onCancel }) {
  const blocRef = useBlocRefHook('emailChangeBloc');
  const classes = useStyles();

  const [stepIndex, setStepIndex] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [errMessage, setErrMessage] = React.useState('');

  const [isTokenValid, setIsTokenValid] = React.useState(false);
  const [mfaNumber, setMfaNumber] = React.useState('');
  const [email, setEmail] = React.useState('');

  React.useEffect(() => {
    blocRef.current.checkCurrentState();

    const stepIdxSub = blocRef.current.stepIndex$.subscribe(setStepIndex);
    const loadingSub = blocRef.current.loading$.subscribe(setLoading);
    const errMessageSub = blocRef.current.errMessage$.subscribe(setErrMessage);
    const tokenValidSub = blocRef.current.isTokenValid$.subscribe(setIsTokenValid);

    const mfaNumberSub = blocRef.current.mfaNumber$.subscribe(setMfaNumber);
    const emailSub = blocRef.current.email$.subscribe(setEmail);

    return () => {
      stepIdxSub.unsubscribe();
      loadingSub.unsubscribe();
      errMessageSub.unsubscribe();

      tokenValidSub.unsubscribe();
      mfaNumberSub.unsubscribe();
      emailSub.unsubscribe();
    };
  }, []);

  const handleMfaNumberChange = React.useCallback((e) => {
    e.preventDefault();

    let { value } = e.target;
    const { inputType } = e?.nativeEvent || {};

    if (inputType === 'insertFromPaste') {
      value = value.replaceAll(' ', '');
    }

    blocRef.current.setMfaNumber(value);
  }, []);

  const handleClickProceed = React.useCallback(() => {
    blocRef.current.proceedClick();
  }, []);

  const handleEmailChange = React.useCallback((e) => {
    e.preventDefault();
    const { value } = e.target;
    blocRef.current.setEmail(value);
  }, []);

  const handleClickOk = React.useCallback(() => {
    blocRef.current.changeRequestSuccess();
    onCancel();
  }, [onCancel]);

  const proceedText = React.useMemo(() => {
    if (stepIndex === 1) return 'Verify';
    if (stepIndex === 2) return 'Change Email';
    return 'Proceed';
  }, [stepIndex]);

  const isProceedEnabled = () => {
    if (stepIndex === 1) {
      if (mfaNumber?.length !== 6) return false;
      return true;
    }

    if (stepIndex === 2) {
      if (mfaNumber?.length !== 6 || !email) return false;
      return true;
    }

    return true;
  };

  return (
    <Container style={{ background: 'white', width: '400px' }}>
      <div style={{ paddingBlock: '20px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Typography className="page-header mb-1">Email Change Request</Typography>
        </div>

        <div style={{ marginBottom: '16px' }}>
          {stepIndex === 0 && (
            <Typography>
              You are about to change your email address. We need to verify your identity before we can continue the process.
              <br /> <br />
              Click the <span style={{ fontWeight: 'bold' }}>Proceed</span> button below to continue. If you do not wish to change your email address, click{' '}
              <span style={{ fontWeight: 'bold' }}>Cancel</span>.
            </Typography>
          )}

          {stepIndex === 1 && (
            <div>
              <Typography>We have sent a verification code to your email. Please enter the code below to continue.</Typography>

              <div style={{ marginTop: '16px' }}>
                <span className={classes.inputLabel}>Enter verification code:</span>
                <input
                  className={classes.inputClass}
                  value={mfaNumber}
                  disabled={loading || stepIndex !== 1 || isTokenValid}
                  onChange={handleMfaNumberChange}
                  placeholder="XXXXXX"
                />
              </div>
            </div>
          )}

          {stepIndex === 2 && (
            <div>
              <Typography>
                We confirmed that the code you entered is correct. Please enter your new email address below and click <span style={{ fontWeight: 'bold' }}>Change Email</span> to
                continue.
              </Typography>

              <div style={{ marginTop: '16px' }}>
                <span className={classes.mfaCode} onChange={handleMfaNumberChange}>
                  {mfaNumber}
                </span>
              </div>

              <div style={{ marginTop: '16px' }}>
                <span className={classes.inputLabel}>Email</span>
                <input className={classes.emailInput} value={email} disabled={loading} onChange={handleEmailChange} placeholder="yournewemail@domain.com" />
              </div>
            </div>
          )}

          {stepIndex === 3 && (
            <div>
              <div style={{ alignItems: 'start', background: '#f0fdf4', border: '1px solid #16a34a', borderRadius: '8px', display: 'flex', flexWrap: 'nowrap', padding: '12px' }}>
                <CheckCircle style={{ color: '#22c55e', marginRight: '8px' }} />
                <Typography style={{ color: '#22c55e', margin: 0 }}>Email change request approved!</Typography>
              </div>

              <Typography style={{ marginTop: '12px' }}>
                We have sent a verification link to your new email address.{' '}
                <span style={{ fontWeight: 'bold' }}>Please click on the link that has just been sent to your email address</span> to confirm and complete the process. Click the
                button given below and you will be automatically logged out.
              </Typography>
            </div>
          )}

          {errMessage ? (
            <div className={classes.errMessageContainer}>
              <span>{errMessage}</span>
            </div>
          ) : null}
        </div>

        {stepIndex !== 3 ? (
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between' }}>
            <Button variant="outlined" disabled={loading} color="primary" onClick={onCancel} style={{ marginRight: '8px', marginTop: '4px' }}>
              Cancel
            </Button>

            <Button
              onClick={handleClickProceed}
              disabled={loading || !isProceedEnabled() || errMessage}
              variant="contained"
              component="span"
              color="primary"
              style={{ marginTop: '4px' }}>
              {proceedText}
            </Button>
          </div>
        ) : (
          <div style={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            <Button variant="contained" disabled={loading} color="primary" onClick={handleClickOk} style={{ marginTop: '4px' }}>
              OK
            </Button>
          </div>
        )}
      </div>
    </Container>
  );
}
